/**
 * CookieBanner — LGPD (Lei 13.709/2018) + Consent Mode V2
 *
 * Launch-06 (08/06/2026):
 * - Aparece na primeira visita (localStorage `seraik_consent` ausente).
 * - 3 botões: Aceitar Tudo / Rejeitar Não-Essenciais / Personalizar.
 * - Persiste decisão em localStorage + dispara `gtag('consent', 'update', ...)` se gtag existir.
 * - Versionado (`CONSENT_VERSION`) — se atualizarmos política, banner reaparece.
 *
 * Texto placeholder pra revisão jurídica final.
 */
'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';

const STORAGE_KEY = 'seraik_consent';
const CONSENT_VERSION = '2026-06-08';

type Categories = {
  essential: true; // sempre on
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

interface ConsentRecord {
  version: string;
  timestamp: string;
  categories: Categories;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

function readConsent(): ConsentRecord | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(categories: Omit<Categories, 'essential'>): void {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: { essential: true, ...categories },
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {}

  // Google Consent Mode V2 — atualiza estado pro gtag se carregado.
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage:            categories.marketing ? 'granted' : 'denied',
      ad_user_data:          categories.marketing ? 'granted' : 'denied',
      ad_personalization:    categories.marketing ? 'granted' : 'denied',
      analytics_storage:     categories.analytics ? 'granted' : 'denied',
      functionality_storage: categories.preferences ? 'granted' : 'denied',
      personalization_storage: categories.preferences ? 'granted' : 'denied',
      security_storage:      'granted', // sempre — anti-fraude
    });
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<'compact' | 'detailed'>('compact');
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [preferences, setPreferences] = useState(true);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setVisible(true);
  }, []);

  function accept(all: boolean) {
    const cats = all
      ? { analytics: true, marketing: true, preferences: true }
      : { analytics, marketing, preferences };
    writeConsent(cats);
    setVisible(false);
  }

  function rejectNonEssential() {
    writeConsent({ analytics: false, marketing: false, preferences: false });
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Aviso de cookies"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background/95 backdrop-blur shadow-2xl"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-sm font-semibold mb-1">Privacidade dos seus dados</h2>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Usamos cookies essenciais para autenticação e segurança. Cookies de analytics
              e marketing são opcionais — você pode aceitar, recusar ou personalizar.
              Saiba mais em{' '}
              <Link href="/cookies" className="underline hover:text-foreground">
                /cookies
              </Link>{' '}
              e{' '}
              <Link href="/privacy" className="underline hover:text-foreground">
                /privacy
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={rejectNonEssential}
            aria-label="Fechar e rejeitar opcionais"
            className="p-1.5 -mr-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {mode === 'detailed' && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
            <label className="flex items-start gap-2 p-3 rounded-md border border-border bg-muted/30 opacity-70">
              <input type="checkbox" checked disabled className="mt-0.5" />
              <div>
                <div className="font-medium">Essenciais (obrigatório)</div>
                <div className="text-muted-foreground">Login, segurança, preferências básicas. Não dá pra desativar.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-3 rounded-md border border-border cursor-pointer hover:border-blue-500/40">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Analytics</div>
                <div className="text-muted-foreground">Métricas de uso pra melhorar produto. Sem identificação individual.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-3 rounded-md border border-border cursor-pointer hover:border-blue-500/40">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Marketing</div>
                <div className="text-muted-foreground">Anúncios personalizados e remarketing. Default off.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-3 rounded-md border border-border cursor-pointer hover:border-blue-500/40">
              <input
                type="checkbox"
                checked={preferences}
                onChange={(e) => setPreferences(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Preferências</div>
                <div className="text-muted-foreground">Tema, idioma, último workspace selecionado.</div>
              </div>
            </label>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          {mode === 'compact' && (
            <button
              type="button"
              onClick={() => setMode('detailed')}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-muted"
            >
              Personalizar
            </button>
          )}
          <button
            type="button"
            onClick={rejectNonEssential}
            className="px-3 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-muted"
          >
            Recusar opcionais
          </button>
          {mode === 'detailed' && (
            <button
              type="button"
              onClick={() => accept(false)}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-blue-500/40 text-blue-600 hover:bg-blue-500/10"
            >
              Salvar minhas escolhas
            </button>
          )}
          <button
            type="button"
            onClick={() => accept(true)}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Aceitar tudo
          </button>
        </div>
      </div>
    </div>
  );
}
